import * as React from 'react';
import { cn, getTestId } from '../utils';

type BaseButtonAttributes = Pick<
  React.ButtonHTMLAttributes<HTMLButtonElement>,
  'className' | 'id' | 'disabled'
>;

export interface LanguageSelectorProps extends BaseButtonAttributes {
  /**
   * Currently active language code, e.g. `'cs'` or `'en'`.
   */
  currentLanguage: string;
  /**
   * Ordered list of available language codes.
   * The button cycles through them in order.
   * @default ['cs', 'en']
   */
  languages?: string[];
  /**
   * Called with the next language code when the user clicks the button.
   * May be synchronous or asynchronous.
   */
  onChange: (lang: string) => void | Promise<void>;
  /** Test ID for automated tests. */
  testId?: string;
}

export const LanguageSelector = React.forwardRef<HTMLButtonElement, LanguageSelectorProps>(
  (
    { currentLanguage, languages = ['cs', 'en'], onChange, className, testId, id, ...props },
    ref
  ) => {
    const idx = languages.indexOf(currentLanguage);
    const nextLanguage =
      languages.length > 0 ? languages[(idx + 1) % languages.length] : currentLanguage;

    const handleClick = () => {
      void onChange(nextLanguage);
    };

    const activeLabel = currentLanguage === 'cs' ? 'Čeština' : 'English';
    const nextDisplayLabel = nextLanguage === 'cs' ? 'Čeština' : 'English';

    return (
      <button
        ref={ref}
        type='button'
        onClick={handleClick}
        data-testid={getTestId({ testId, id })}
        className={cn(
          'flex items-center gap-xs px-sm py-sm rounded-lg',
          'hover:bg-background-muted transition-colors',
          'text-sm font-medium min-w-15',
          className
        )}
        aria-label={`Switch language to ${nextDisplayLabel}`}
        title={`Current: ${activeLabel}. Click to switch.`}
        {...props}
      >
        {languages.map((lang, index) => (
          <React.Fragment key={lang}>
            {index > 0 && <span className='text-foreground-muted text-xs'>/</span>}
            <span
              className={cn(
                'px-sm py-2xs rounded text-xs font-semibold transition-all',
                lang === currentLanguage
                  ? 'bg-primary-base text-foreground-white'
                  : 'text-foreground-muted'
              )}
            >
              {lang.toUpperCase()}
            </span>
          </React.Fragment>
        ))}
      </button>
    );
  }
);

LanguageSelector.displayName = 'LanguageSelector';
